import React from 'react';

import * as analytics from 'analytics/Analytics';
import AntibodyCreateModal from 'antibodies/actions/AntibodyCreateModal';
import {useImplementedAntibodySchemaInterfacesQuery} from 'antibodies/hooks/useImplementedAntibodySchemaInterfacesQuery';
import NestedCreateAndRegisterAntibodiesModal from 'antibodies/nested-data-creation/NestedCreateAndRegisterAntibodiesModal';
import {assert} from 'Asserts';
import {RegistryFeature} from 'auth/capabilities/CapabilityFeatureConstants';
import {isFeatureEnabled} from 'auth/feature-enablement/FeatureStatus';
import {Permissions} from 'auth/PermissionConstants';
import BenchlingConfig from 'BenchlingConfig';
import {StringIDFolder} from 'constants/FolderConstants';
import ModelTypeConstants from 'constants/ModelTypeConstants';
import {CreateDropdownOption, getFolderCreateDropdownOptions} from 'file-browser/CreateDropdownConstants';
import CreateBioentityModal from 'folder-items/CreateBioentityModal';
import FolderDefaults, {FolderDefaultsKey} from 'FolderDefaults';
import normalizeFolder from 'folders/util/normalizeFolder';
import {ActionOrigin} from 'graphql-client/__generated/graphqlGlobalTypes';
import {useSkippableBenchlingQuery} from 'graphql-client/BenchlingQuery';
import {
  CreateMenuComplexPolymersQuery,
  CreateMenuComplexPolymersQueryVariables,
} from 'modbio/complex-polymers/__generated/CreateMenuComplexPolymersQuery';
import {
  CREATE_MENU_COMPLEX_POLYMERS_QUERY,
  getComplexPolymerSchemaConfigs,
  isComplexPolymerSchema,
  useComplexPolymerSchemasAsCreateDropdownOptions,
} from 'modbio/complex-polymers/ComplexPolymerSchemaUtil';
import useTagSchemaAwareObjectCreateQuery from 'nav/sidepanel/listing/hooks/useTagSchemaAwareObjectCreateQuery';
import {
  getProcessTaskSchemaContextualCreateOptions,
  useProcessTaskSchemasForCreate,
} from 'processes/CreateProcessUtil';
import * as CreateBioentityUtil from 'registry/create-bioentity/CreateBioentityUtil';
import * as RegistryUtil from 'registry/RegistryUtil';
import {SearchParams} from 'search/SearchConstants';
import {useStudySchemasAsCreateDropdownOptions} from 'studies/sidepanel/CreateStudyMenu';
import {toModelTypeConstant} from 'tag-schema/util/ModelTypeConversions';
import Button, {ButtonSize, ButtonStyle} from 'ui-library/buttons/Button';
import Dropdown from 'ui-library/dropdowns/Dropdown';
import Tooltip from 'ui-library/floating-ui/Tooltip';
import {BenchlingIconName} from 'ui-library/icons/BenchlingIcon';
import {MenuHeight, MenuWidth, OptionData} from 'ui-library/menus/MenuConstants';
import SearchableMenu from 'ui-library/menus/SearchableMenu';
import runModal from 'ui-library/runModal';
import {getRenderStarredOptionContent} from 'util/getRenderStarredOptionContent';
import sortedBy from 'util/sortedBy';

import {TagSchemaListingGraphQL} from '../../graphql-fragments/schemas/__generated/TagSchemaListingGraphQL';

type BfiOrMixture = ModelTypeConstants.BASIC_FOLDER_ITEM | ModelTypeConstants.MIXTURE;
const LOADING_OPTION = {name: 'Loading...', value: 'LOADING_SENTINEL', disabled: true};
const ACTION_ORIGIN = ActionOrigin.PROJECTS_SIDEPANEL;

function getPermissions(folder: StringIDFolder): Permissions | null {
  if (!folder || !folder.source) {
    return null;
  }
  assert(
    !RegistryUtil.isRegistry(folder.source),
    "Did not expect folder to be in the Registry. FolderCreateDropdown doesn't support the Registry yet."
  );
  let permissions;
  if (folder.permissions) {
    permissions = folder.permissions;
  }
  return permissions || null;
}

/**
 * Create a list of menu items for schemas of a particular folder item type.
 * TODO: Unify with CreateMenu.getTagSchemaMenuItems
 * @param customHandleClick By default, the handleClick function of the returned menu items will launch
 *                          CreateBioentityModal. In order to override that behavior, pass a function that,
 *                          when called with a tag schema, returns a function with the desired handleClick
 *                          behavior for a click on that schema's menu item.
 * @param folderItemType The folder item type to filter by, e.g. ModelTypeConstants.BASIC_FOLDER_ITEM
 * @param tagSchemas All tag schemas available to the current user
 * @param starredTagSchemaIds The API IDs of all tag schemas the current user has starred
 * @param schemaFilterFn An optional predicate that schemas must pass to be included in the list
 */
export function getTagSchemaMenuOptions({
  customHandleClick,
  folderItemType,
  tagSchemas,
  starredTagSchemaIds,
  schemaFilterFn,
  isLoading,
}: {
  customHandleClick?: (tagSchema: TagSchemaListingGraphQL) => () => void;
  folderItemType: BfiOrMixture;
  tagSchemas: Array<TagSchemaListingGraphQL> | null;
  schemaFilterFn?: (tagSchema: TagSchemaListingGraphQL) => boolean;
  starredTagSchemaIds: Array<string> | null;
  isLoading?: boolean;
}): Array<CreateDropdownOption> {
  if (!tagSchemas) {
    return [];
  } else if (isLoading) {
    return [LOADING_OPTION];
  }
  let filteredSchemas = tagSchemas.filter(
    (tagSchema) =>
      toModelTypeConstant(tagSchema.folderItemType) === folderItemType &&
      tagSchema.permissions.objectsCreatable
  );
  if (schemaFilterFn) {
    filteredSchemas = filteredSchemas.filter((tagSchema) => schemaFilterFn(tagSchema));
  }
  const tagSchemaMenuOptions = filteredSchemas.map((tagSchema) => {
    const handleClick =
      customHandleClick != null
        ? customHandleClick(tagSchema)
        : (): void => {
            CreateBioentityModal.open(CreateBioentityModal, {
              createBioentityArgs: {
                ...CreateBioentityUtil.getDefaultCreateBioentityArgs(),
                tagSchema,
                folderId: FolderDefaults.get(FolderDefaultsKey.INVENTORY),
              },
            });
          };
    return {
      name: tagSchema.name,
      value: tagSchema.id,
      icon: tagSchema.iconId as BenchlingIconName,
      className: 'scrollable newDropdown-schemaOption',
      starrable: true,
      handleClick,
    };
  });
  if (!starredTagSchemaIds) {
    return tagSchemaMenuOptions;
  }
  return sortedBy(tagSchemaMenuOptions, [
    (option) => (starredTagSchemaIds.includes(option.value) ? 'a' : 'b'),
  ]);
}

function makeOptionData(option: CreateDropdownOption): OptionData {
  return {
    name: option.name,
    value: option.value,
    disabled: option.disabled,
    isHeader: option.isHeader,
    icon: option.icon,
    submenu: option.menu
      ? [option.menu.map((menuOption: CreateDropdownOption) => makeOptionData(menuOption))]
      : undefined,
    className: option.className,
    extra: {unstarrable: !option.starrable},
    dataTestAttrs: option.dataTestAttrs,
  };
}

function mapOptionValueToClickHandler(option: CreateDropdownOption): {[value: string]: () => void} {
  if (!option.handleClick) {
    return {};
  }
  return {
    [option.value]: () => {
      option.handleClick!();
    },
  };
}

function mapOptionValuesToClickHandlers(options: Array<CreateDropdownOption>): {
  [value: string]: () => void;
} {
  return options.reduce(
    (optionValuesToClickHandlers: {[value: string]: () => void}, option: CreateDropdownOption) => {
      const optionValueToClickHandler = mapOptionValueToClickHandler(option);
      const nestedOptionValuesToClickHandlers = option.menu
        ? mapOptionValuesToClickHandlers(option.menu)
        : {};
      return {
        ...optionValuesToClickHandlers,
        ...optionValueToClickHandler,
        ...nestedOptionValuesToClickHandlers,
      };
    },
    {}
  );
}

function makeOnSelectCallback(optionValuesToClickHandlers: {
  [value: string]: () => void;
}): (selectedItemValue: string) => void {
  return (selectedItemValue: string) => {
    assert(
      optionValuesToClickHandlers.hasOwnProperty(selectedItemValue),
      `${selectedItemValue} has no associated click handler.`
    );
    optionValuesToClickHandlers[selectedItemValue]();
  };
}

export interface FolderCreateDropdownProps {
  dropLeft: boolean;
  folder: StringIDFolder;
  onOpenFolder: (folder: StringIDFolder, searchParams?: SearchParams) => void;
}

export interface FolderCreateDropdownEnhancedProps {
  folder: StringIDFolder;
  starredSchemaIds: Array<string> | null;
  toggleTagSchemaStar: (tagSchemaId: string) => void;
  tagSchemas: Array<TagSchemaListingGraphQL> | null;
}

export function FolderCreateDropdown(
  props: FolderCreateDropdownProps & FolderCreateDropdownEnhancedProps
): JSX.Element {
  // Fetch a bunch of stuff that we need to populate the dropdown but don't get for free with
  // TagSchemaListingQuery.
  const complexPolymerCreateOptions = useComplexPolymerSchemasAsCreateDropdownOptions(props.folder.id);
  const complexPolymersSchemasResult = useSkippableBenchlingQuery<
    CreateMenuComplexPolymersQuery,
    CreateMenuComplexPolymersQueryVariables
  >(CREATE_MENU_COMPLEX_POLYMERS_QUERY, {
    fetchPolicy: 'cache-and-network',
    skip: !BenchlingConfig.MODBIO_ENABLE_COMPOSITE_ENTITIES,
  });
  const {implementedAntibodySchemaInterfaces} = useImplementedAntibodySchemaInterfacesQuery();
  const processTaskSchemas = useProcessTaskSchemasForCreate();
  const studySchemaCreateOptions = useStudySchemasAsCreateDropdownOptions(ACTION_ORIGIN, {
    initialFolderId: props.folder.id,
  });

  const allComplexPolymerSchemaConfigs = getComplexPolymerSchemaConfigs(complexPolymersSchemasResult);
  const antibodySchemaIds = new Set<string>(implementedAntibodySchemaInterfaces.map(({schema: {id}}) => id));
  const permissions = getPermissions(props.folder);
  const tagSchemas = props.tagSchemas || [];

  const options = getFolderCreateDropdownOptions({
    antibodySchemaOptions: [
      ...(BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_NESTED_DATA_CREATION
        ? [
            {
              name: 'Create antibodies',
              value: 'create-antibodies',
              icon: 'import' as BenchlingIconName,
              handleClick: () => {
                runModal(NestedCreateAndRegisterAntibodiesModal, {
                  initialProjectFolderId: props.folder.id,
                  analyticsMetadata: {
                    actionOrigin: ACTION_ORIGIN,
                    eventGroupId: analytics.makeAnalyticsEventId(),
                  },
                });
              },
            },
          ]
        : []),
      ...(BenchlingConfig.MOLBIO_ENABLE_ANTIBODY_SINGLE_CREATE
        ? [
            {
              name: 'Create single antibody',
              value: 'create-single-antibody',
              icon: 'antibody' as BenchlingIconName,
              handleClick: () => {
                runModal(AntibodyCreateModal, {initialFolderId: props.folder.id});
              },
            },
          ]
        : []),
    ],
    bfiSchemaOptions: getTagSchemaMenuOptions({
      folderItemType: ModelTypeConstants.BASIC_FOLDER_ITEM,
      tagSchemas,
      schemaFilterFn: (tagSchema: TagSchemaListingGraphQL) =>
        !antibodySchemaIds.has(tagSchema.id) &&
        !isComplexPolymerSchema(allComplexPolymerSchemaConfigs, tagSchema.id),
      starredTagSchemaIds: props.starredSchemaIds,
    }),
    complexPolymerSchemaConfigs: allComplexPolymerSchemaConfigs,
    complexPolymerSchemaOptions: complexPolymerCreateOptions,
    folder: normalizeFolder(props.folder),
    handleOpenFolder: props.onOpenFolder,
    mixtureSchemaOptions: isFeatureEnabled({feature: RegistryFeature.REGISTRY_MIXTURES})
      ? getTagSchemaMenuOptions({
          folderItemType: ModelTypeConstants.MIXTURE,
          tagSchemas,
          starredTagSchemaIds: props.starredSchemaIds,
        })
      : [],
    permissions,
    processSchemaOptions: getProcessTaskSchemaContextualCreateOptions(processTaskSchemas, props.folder.id),
    studySchemaOptions: studySchemaCreateOptions,
  });
  const optionValueToClickHandlers = mapOptionValuesToClickHandlers(options);
  const optionDatas = options.map((menuOption) => makeOptionData(menuOption));

  const menu = (
    <SearchableMenu
      onSelect={makeOnSelectCallback(optionValueToClickHandlers)}
      optionDatas={optionDatas}
      renderOptionContent={getRenderStarredOptionContent(props.toggleTagSchemaStar, props.starredSchemaIds, {
        getId: (optionData: OptionData) => optionData.value,
      })}
      height={MenuHeight.LARGE}
      width={MenuWidth.LARGE}
      dropLeft={props.dropLeft}
    />
  );

  const enableCreateButton = permissions && (permissions.subfolderCreatable || permissions.appendable);
  return (
    <Dropdown closeOnInsideClick={true} closeOnOutsideClick={true} menu={menu}>
      <Button
        className="u-mini-spacing-left"
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon="compose"
        size={ButtonSize.SMALL}
        disabled={!enableCreateButton}
      />
    </Dropdown>
  );
}

function LoadingIconButton({icon}: {icon: BenchlingIconName}): JSX.Element {
  // Should resemble the dropdown button in FolderCreateDropdown, but disabled
  return (
    <Tooltip text="Loading..." disableContentSelection={true}>
      <Button
        className="u-mini-spacing-left"
        buttonStyle={ButtonStyle.TERTIARY_BLUE}
        icon={icon}
        size={ButtonSize.SMALL}
        disabled={true}
      />
    </Tooltip>
  );
}

export default function QueryingFolderCreateDropdown(props: FolderCreateDropdownProps): JSX.Element | null {
  const {folder, ...restProps} = props;
  const result = useTagSchemaAwareObjectCreateQuery(folder);

  if (result == null) {
    return <LoadingIconButton icon="compose" />;
  } else {
    const {tagSchemas, starredTagSchemaIds, handleToggleTagSchemaStar, folder: modifiedFolder} = result;

    return (
      <FolderCreateDropdown
        {...restProps}
        starredSchemaIds={starredTagSchemaIds}
        toggleTagSchemaStar={handleToggleTagSchemaStar}
        tagSchemas={tagSchemas}
        folder={modifiedFolder}
      />
    );
  }
}
